import React, { useState, useEffect } from 'react';
import { FileText, Image as ImageIcon, Download, Eye, X, ExternalLink } from 'lucide-react';

const API_BASE = (import.meta.env.VITE_API_URL || 'https://cbrsancarlos.cl').replace(/\/$/, '');

export const getFileType = (filename = '') => {
  const ext = filename.split('.').pop().toLowerCase();
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['docx', 'doc'].includes(ext)) return 'word';
  return 'other';
};

export const getDocUrl = (token) => {
  return `${API_BASE}/api/v1/tramites/documentos/${token}`;
};

export const getWordViewerUrl = (token) => {
  const directUrl = getDocUrl(token);
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(directUrl)}`;
};

export default function VisualizadorDocumentosModal({
  isOpen = true,
  onClose,
  tramite,
  initialDoc = null,
  zIndex = 100005,
}) {
  const documentos = tramite?.documentos || [];
  const [selectedDoc, setSelectedDoc] = useState(initialDoc || documentos[0] || null);

  useEffect(() => {
    if (initialDoc) {
      setSelectedDoc(initialDoc);
    } else if (documentos.length > 0 && !selectedDoc) {
      setSelectedDoc(documentos[0]);
    }
  }, [initialDoc, documentos]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!isOpen || !tramite) return null;

  const currentType = selectedDoc ? getFileType(selectedDoc.nombre_original) : null;
  const currentUrl = selectedDoc ? getDocUrl(selectedDoc.token_descarga) : null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex,
        padding: '1rem',
        animation: 'fadeIn 0.2s ease-out',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '1080px',
          width: '95vw',
          height: '88vh',
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(to right, #f8fafc, #eff6ff)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                background: '#dbeafe',
                color: '#1d4ed8',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <FileText size={22} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: '#0f172a' }}>
                Visualizador de Documentos
              </h3>
              <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 2 }}>
                Trámite #{tramite.numero_seguimiento || tramite.id} · {tramite.nombre_solicitante || 'Ciudadano'}
                {documentos.length > 0 && ` (${documentos.length} documento${documentos.length > 1 ? 's' : ''})`}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: '#f1f5f9',
              border: 'none',
              borderRadius: '8px',
              width: 32,
              height: 32,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#475569',
              transition: 'background 0.2s',
            }}
            title="Cerrar visualizador"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content Body */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minHeight: 0 }}>
          {/* Sidebar List */}
          <div
            style={{
              width: '320px',
              borderRight: '1px solid #e2e8f0',
              backgroundColor: '#f8fafc',
              overflowY: 'auto',
              padding: '1rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              flexShrink: 0,
            }}
          >
            <div
              style={{
                fontSize: '0.75rem',
                fontWeight: 700,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: 4,
              }}
            >
              Archivos Adjuntos
            </div>

            {documentos.length > 0 ? (
              documentos.map((doc, idx) => {
                const type = getFileType(doc.nombre_original);
                const isSelected = selectedDoc?.id ? selectedDoc.id === doc.id : selectedDoc?.token_descarga === doc.token_descarga;

                return (
                  <div
                    key={doc.id || doc.token_descarga || idx}
                    onClick={() => setSelectedDoc(doc)}
                    style={{
                      padding: '10px 12px',
                      backgroundColor: isSelected ? '#ffffff' : 'transparent',
                      border: isSelected ? '1.5px solid #2563eb' : '1px solid #e2e8f0',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      boxShadow: isSelected ? '0 4px 12px rgba(37, 99, 235, 0.08)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        color: isSelected ? '#2563eb' : '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {type === 'image' && <ImageIcon size={20} />}
                      {type === 'pdf' && <FileText size={20} />}
                      {type === 'word' && <FileText size={20} />}
                      {type === 'other' && <Download size={20} />}
                    </div>

                    <div style={{ overflow: 'hidden', flex: 1 }}>
                      <div
                        style={{
                          fontSize: '0.82rem',
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? '#0f172a' : '#334155',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={doc.nombre_original}
                      >
                        {doc.nombre_original}
                      </div>
                      <div
                        style={{
                          fontSize: '0.7rem',
                          color: '#64748b',
                          textTransform: 'uppercase',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginTop: 2,
                        }}
                      >
                        <span>{type}</span>
                        {doc.repertorio && (
                          <span
                            style={{
                              padding: '1px 5px',
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              borderRadius: '4px',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                            }}
                          >
                            Rep. #{doc.repertorio.numero_repertorio}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#94a3b8', fontSize: '0.85rem' }}>
                No hay documentos adjuntos en este trámite.
              </div>
            )}
          </div>

          {/* Viewer Area */}
          <div
            style={{
              flex: 1,
              backgroundColor: '#1e293b',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            {selectedDoc ? (
              (() => {
                if (currentType === 'image') {
                  return (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1.5rem',
                        overflow: 'auto',
                      }}
                    >
                      <img
                        src={currentUrl}
                        alt={selectedDoc.nombre_original}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          borderRadius: '8px',
                          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
                        }}
                      />
                    </div>
                  );
                }

                if (currentType === 'pdf') {
                  return (
                    <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                      <iframe
                        src={`${currentUrl}#toolbar=1`}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                        title={selectedDoc.nombre_original}
                      />
                    </div>
                  );
                }

                if (currentType === 'word') {
                  return (
                    <iframe
                      src={getWordViewerUrl(selectedDoc.token_descarga)}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                      title={selectedDoc.nombre_original}
                    />
                  );
                }

                return (
                  <div
                    style={{
                      textAlign: 'center',
                      backgroundColor: '#ffffff',
                      padding: '2.5rem',
                      borderRadius: '16px',
                      maxWidth: '420px',
                      boxShadow: '0 20px 25px -5px rgba(0,0,0,0.2)',
                    }}
                  >
                    <Download size={52} color="#0284c7" style={{ marginBottom: '1rem' }} />
                    <h3 style={{ margin: '0 0 6px', fontSize: '1.1rem', color: '#0f172a' }}>
                      Formato no previsualizable
                    </h3>
                    <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#64748b' }}>
                      Puedes abrir o descargar <strong>{selectedDoc.nombre_original}</strong> en tu equipo.
                    </p>
                    <a
                      href={`${currentUrl}/descargar`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-primary"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        textDecoration: 'none',
                        padding: '8px 16px',
                        fontWeight: 600,
                        fontSize: '0.85rem',
                      }}
                    >
                      <Download size={15} /> Descargar Archivo
                    </a>
                  </div>
                );
              })()
            ) : (
              <div style={{ textAlign: 'center', color: '#94a3b8' }}>
                <Eye size={56} style={{ marginBottom: '0.75rem', opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: '0.9rem' }}>Selecciona un documento lateral para visualizarlo</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '0.85rem 1.5rem',
            backgroundColor: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            {selectedDoc && (
              <span style={{ fontSize: '0.8rem', color: '#475569', fontWeight: 500 }}>
                Archivo: <strong>{selectedDoc.nombre_original}</strong>
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {selectedDoc && (
              <>
                <a
                  href={currentUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    color: '#334155',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                  title="Abrir en pestaña independiente"
                >
                  <ExternalLink size={14} /> Abrir en pestaña
                </a>
                <a
                  href={`${currentUrl}/descargar`}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    backgroundColor: '#ffffff',
                    color: '#0284c7',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  <Download size={14} /> Descargar
                </a>
              </>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              style={{ padding: '6px 16px', borderRadius: '8px', fontWeight: 600, fontSize: '0.8rem' }}
              onClick={onClose}
            >
              Cerrar Visualizador
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
